import React, { forwardRef } from 'react';
import { useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';

import { Collapse, List } from '@mui/material';

import { AuthorizationContext } from 'app/components/Authorization';
import { useStoreSelector } from 'app/store';
import type { AuthorizationContextInterface } from 'app/components/Authorization/AuthorizationContextInterface';

import type { Page } from '../../Sidebar.types';
import {
  ButtonStyled,
  ButtonToggleExpandStyled,
  ExpandLessIconStyled,
  ExpandMoreIconStyled,
  FontAwesomeIconStyled,
  ListItemChildrenStyled,
  ListItemStyled,
} from './SidebarPageGroup.styles';

type SidebarPageGroupProps = {
  page: Page;
};

type PermissionMap = Record<string, { permissions: string[] }>;

const CustomRouterLink = (props: any, ref: any) => (
  <div ref={ref} style={{ flexGrow: 1 }}>
    <NavLink {...props} />
  </div>
);

const CustomRouterLinkWithRef = forwardRef(CustomRouterLink);

export const SidebarPageGroup = ({ page }: SidebarPageGroupProps) => {
  const { pathname: currentPath } = useLocation();
  const { permissions }: AuthorizationContextInterface = React.useContext(AuthorizationContext);
  const permissionsByEntity = permissions as PermissionMap | null | undefined;
  const { user } = useStoreSelector(state => state.AppReducer);
  const [toggleChildren, setToggleChildren] = React.useState(false);

  const hasAccessTo = React.useCallback(
    (entity: string, permission: string, userId?: string) => {
      if (userId === user.userId) {
        return true;
      }

      return !!permissionsByEntity?.[entity]?.permissions.includes(permission);
    },
    [permissionsByEntity, user.userId],
  );

  const children = (page?.children ?? []).filter(item => hasAccessTo(item.entity, item.permission));

  const handleToggle = React.useCallback(() => setToggleChildren(!toggleChildren), [toggleChildren]);

  React.useEffect(() => {
    const toggle = page.children ? page.children.some(child => child.href === currentPath) : false;
    setToggleChildren(toggle);
  }, [currentPath, page]);

  if (page.entity && page.permission) {
    if (!hasAccessTo(page.entity, page.permission)) {
      if (children.length === 0) {
        return <></>;
      }

      return (
        <>
          {children.map(child => (
            <ListItemStyled
              disableGutters
              key={child.href}
              data-testid="navbar-children-item"
              id={'navbar-children-item'}
            >
              <ButtonStyled
                // @ts-ignore
                component={CustomRouterLinkWithRef}
                to={child.href}
                variant={'text'}
              >
                <FontAwesomeIconStyled icon={page.iconName} />
                {child.title}
              </ButtonStyled>
            </ListItemStyled>
          ))}
        </>
      );
    }
  }

  return (
    <List disablePadding>
      <ListItemStyled disableGutters>
        <ButtonStyled
          data-testid="navbar-item"
          // @ts-ignore
          component={CustomRouterLinkWithRef}
          to={page.href}
          variant={'text'}
        >
          <FontAwesomeIconStyled icon={page.iconName} />
          {page.title}
        </ButtonStyled>
        {children.length > 0 && (
          <ButtonToggleExpandStyled onClick={handleToggle} variant={'text'}>
            {toggleChildren ? <ExpandLessIconStyled /> : <ExpandMoreIconStyled />}
          </ButtonToggleExpandStyled>
        )}
      </ListItemStyled>
      {children.length > 0 && (
        <Collapse in={toggleChildren} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map(page => (
              <ListItemChildrenStyled disableGutters key={page.href}>
                <ButtonStyled
                  data-testid="navbar-item-child"
                  // @ts-ignore
                  component={CustomRouterLinkWithRef}
                  to={page.href}
                  variant={'text'}
                >
                  {page.title}
                </ButtonStyled>
              </ListItemChildrenStyled>
            ))}
          </List>
        </Collapse>
      )}
    </List>
  );
};
